import { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Spinner } from '../components/ui/spinner';
import { MapLocationButton } from '../components/MapLocationButton';
import { Landmark, MapPin, Phone, Mail, Globe, ExternalLink, Search } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import axios from 'axios';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const cultureIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count >= 100 ? 50 : count >= 10 ? 40 : 30;
  return L.divIcon({
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;box-shadow:0 4px 12px rgba(0,0,0,0.3);background-color:#9333ea;">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

export function CulturePage() {
  const { language } = useLanguage();
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const t = {
    title: language === 'es' ? 'Cultura y Educación' : 'Culture & Education',
    subtitle: language === 'es' ? 'Centros educativos y culturales de Tenerife' : 'Educational and cultural centers in Tenerife',
    searchPlaceholder: language === 'es' ? 'Buscar por nombre o municipio...' : 'Search by name or municipality...',
    showing: language === 'es' ? 'Mostrando' : 'Showing',
    of: language === 'es' ? 'de' : 'of',
    results: language === 'es' ? 'resultados' : 'results',
    allTypes: language === 'es' ? 'Todos' : 'All',
    filterByType: language === 'es' ? 'Filtrar por tipo' : 'Filter by type',
    loading: language === 'es' ? 'Cargando datos...' : 'Loading data...',
    openInGoogleMaps: language === 'es' ? 'Abrir en Google Maps' : 'Open in Google Maps',
  };

  // Color palette for education types
  const typeColors = {
    'Enseñanza Infantil Y Primaria': '#3b82f6',
    'Enseñanza Especializada': '#8b5cf6',
    'Centro Cultural': '#ec4899',
    'Biblioteca Ludoteca': '#22c55e',
    'Enseñanza Secundaria': '#f97316',
    'Museos Salas De Arte': '#eab308',
    'Guarderias Centros Infantiles': '#06b6d4',
    'Enseñanza Infantil': '#60a5fa',
    'Enseñanza Profesional': '#a855f7',
    'Enseñanza Idiomas': '#14b8a6',
    'Enseñanza Universitaria': '#6366f1',
    'Enseñanza Especial': '#f43f5e',
    'Enseñanza Adultos': '#84cc16',
    'Enseñanza Obligatoria': '#0ea5e9',
    'Enseñanza Profesorado': '#d946ef',
    'Enseñanza A Distancia': '#64748b',
    'Residencia Educativa': '#78716c',
    'Asociacion Cultural': '#db2777',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/services/education`);
        setEducation(response.data || []);
      } catch (error) {
        console.error('Error fetching education data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get unique types for filter
  const types = useMemo(() => {
    const typesSet = new Set();
    education.forEach(item => {
      if (item.tipo) typesSet.add(item.tipo);
    });
    return Array.from(typesSet).sort();
  }, [education]);

  // Filter data
  const filteredData = useMemo(() => {
    let filtered = education;
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.tipo === typeFilter);
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        (item.nombre || '').toLowerCase().includes(searchLower) ||
        (item.municipio || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [education, typeFilter, search]);

  const markers = useMemo(() => 
    filteredData.filter(item => item.lat && item.lon),
    [filteredData]
  );

  const centerPosition = [28.2916, -16.5291];

  const openInGoogleMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lon}`, '_blank');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Landmark className="w-5 h-5 text-purple-600" />
          <span className="text-sm font-medium text-purple-600 uppercase tracking-wider">
            {language === 'es' ? 'Datos Abiertos' : 'Open Data'}
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#252a34] font-['Outfit'] mb-2" data-testid="culture-title">
          {t.title}
        </h1>
        <p className="text-lg text-[#252a34]/70">{t.subtitle}</p>
      </div>

      {/* Stats */}
      <div className="mb-6 flex items-center gap-4">
        <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200">
          <span className="text-2xl font-bold text-purple-600">{education.length.toLocaleString()}</span>
          <span className="text-gray-600 ml-2">
            {language === 'es' ? 'centros' : 'centers'}
          </span>
        </div>
      </div>

      {/* Type Filters */}
      {!loading && (
        <div className="mb-4">
          <p className="text-sm text-[#252a34]/60 mb-2">{t.filterByType}:</p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                typeFilter === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-[#252a34] border border-slate-200 hover:border-purple-400'
              }`}
            >
              {t.allTypes} ({education.length.toLocaleString()})
            </button>
            {types.map((tipo) => {
              const count = education.filter(e => e.tipo === tipo).length;
              const color = typeColors[tipo] || '#6b7280';
              const isActive = typeFilter === tipo;
              return (
                <button
                  key={tipo}
                  onClick={() => setTypeFilter(tipo)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    isActive
                      ? 'text-white shadow-md'
                      : 'bg-white text-[#252a34] border border-slate-200 hover:shadow-sm'
                  }`}
                  style={isActive ? { backgroundColor: color } : {}}
                >
                  {tipo} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search */}
      {!loading && (
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#252a34]/40" />
            <Input
              type="text"
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-white border-slate-200"
              data-testid="culture-search"
            />
          </div>
          <div className="text-sm text-[#252a34]/60 flex items-center gap-2">
            {t.showing} <strong className="text-purple-600">{filteredData.length.toLocaleString()}</strong> {t.of} {education.length.toLocaleString()} {t.results}
          </div>
        </div>
      )}

      {/* Map */}
      <Card className="overflow-hidden border-slate-200 mb-6" data-testid="culture-map">
        <div className="h-[400px] md:h-[450px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full bg-slate-50">
              <Spinner size="lg" />
              <p className="mt-4 text-[#252a34]/60 text-sm font-medium">{t.loading}</p>
            </div>
          ) : (
            <MapContainer center={centerPosition} zoom={9} style={{ height: '100%', width: '100%' }} className="z-0">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapLocationButton language={language} />
              <MarkerClusterGroup 
                chunkedLoading 
                iconCreateFunction={createClusterIcon} 
                maxClusterRadius={50}
              >
                {markers.map((item, idx) => (
                  <Marker 
                    key={idx} 
                    position={[item.lat, item.lon]} 
                    icon={cultureIcon}
                  >
                    <Popup>
                      <div className="font-['Outfit'] min-w-[220px] max-w-[280px]">
                        <h3 className="font-bold text-[#252a34] mb-1 text-sm">{item.nombre}</h3>
                        <Badge 
                          className="text-xs mb-2 text-white"
                          style={{ backgroundColor: typeColors[item.tipo] || '#6b7280' }}
                        >
                          {item.tipo}
                        </Badge>
                        <div className="text-xs text-[#252a34]/70 space-y-1 mt-2">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span>{item.municipio}</span>
                          </div>
                          {item.direccion && (
                            <p className="text-[#252a34]/60 ml-4">{item.direccion}</p>
                          )}
                          {item.telefono && (
                            <div className="flex items-center gap-1">
                              <Phone className="w-3 h-3 flex-shrink-0" />
                              <a href={`tel:${item.telefono}`} className="text-[#00599c] hover:underline">
                                {item.telefono}
                              </a>
                            </div>
                          )}
                          {item.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{item.email}</span>
                            </div>
                          )}
                          {item.web && (
                            <div className="flex items-center gap-1">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              <a 
                                href={item.web.startsWith('http') ? item.web : `https://${item.web}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#00599c] hover:underline truncate"
                              >
                                Web
                              </a>
                            </div>
                          )}
                        </div>
                        <button 
                          onClick={() => openInGoogleMaps(item.lat, item.lon)}
                          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-3"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {t.openInGoogleMaps}
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>
          )}
        </div>
      </Card>
    </div>
  );
}
